/* eslint-disable @typescript-eslint/naming-convention */
export class WebFamCitation {
    id!: string;
    source_id!: string;
    title?: string;
    text?: string;
    // eslint-disable-next-line id-blacklist
    number?: string;
    year?: string;
    pages?: string;
    type?: string;
    link?: string;
    lookup?: string;
    files?: any[];
    expanded?: boolean;
}

export class WebFamSource {
    id!: string;
    name?: string;
    years?: string;
    author?: string;
    publisher?: string;
    link?: string;
    type?: string;
    citations?: WebFamCitation[];
}

/**
 * Rodzaje źródła. 'vital' (metrykalne) przełącza cytaty w tryb aktów:
 * zamiast wolnego tytułu wybiera się rodzaj zdarzenia z CitationEventTypes.
 * Źródło bez rodzaju zachowuje się jak 'book'/'other' - wolny cytat.
 */
/* `name` to KLUCZ tłumaczenia - potok `t` w szablonie zamienia go na tekst. */
export const SourceTypes = [
    { id: 'vital', name: 'sourceType.vital' },
    { id: 'book', name: 'sourceType.book' },
    { id: 'other', name: 'sourceType.other' },
];

/** Rodzaje zdarzenia (aktu) dla źródeł metrykalnych - citation.type w bazie. */
export const CitationEventTypes = [
    { id: 'birth', name: 'actType.birth' },
    { id: 'baptism', name: 'actType.baptism' },
    { id: 'marriage', name: 'actType.marriage' },
    { id: 'death', name: 'actType.death' },
    { id: 'other', name: 'actType.other' },
];

export function sourceTypeName(type?: string | null): string {
    return SourceTypes.find((t) => t.id === type)?.name ?? '';
}

export function citationEventTypeName(type?: string | null): string {
    return CitationEventTypes.find((t) => t.id === type)?.name ?? '';
}

/** Wiersz narzędzia porządkowania źródeł - źródło z licznikami. */
export class WebFamSourceManage extends WebFamSource {
    citations_count!: number;
    usage_count!: number;
    files_count!: number;
}

/** Wiersz narzędzia porządkowania cytatów - cytat z licznikami. */
export class WebFamCitationManage extends WebFamCitation {
    usage_count!: number;
    files_count!: number;
}

/**
 * Szablon nazwy. `pattern` zawiera „{}" w miejscu, w które wchodzi tekst
 * wpisany przez użytkownika (np. nazwa parafii); wzorzec bez „{}" jest
 * gotową podpowiedzią. `source_type` puste = szablon dla każdego rodzaju.
 */
export class WebFamNameTemplate {
    id!: string;
    kind!: 'source' | 'citation';
    source_type?: string | null;
    pattern!: string;
    item_order?: number;
}

/** Znacznik miejsca na tekst użytkownika w szablonie. */
export const NAME_TEMPLATE_MARK = '{}';

/**
 * Podstawia wpisany tekst w szablon. Przy pustym tekście zostaje sam wzorzec
 * bez osieroconych myślników na krańcach.
 */
export function applyNameTemplate(pattern: string, text: string): string {
    const value = (text ?? '').trim();
    if (!pattern.includes(NAME_TEMPLATE_MARK)) {
        return pattern.trim();
    }
    return pattern.split(NAME_TEMPLATE_MARK).join(value)
        .replace(/\s{2,}/g, ' ')
        .replace(/^[\s\-–—•,]+/, '')
        .replace(/[\s\-–—•,]+$/, '')
        .trim();
}

/** Podpowiedzi dla wpisanego tekstu: szablony danego rodzaju i typu źródła. */
export function nameSuggestions(templates: WebFamNameTemplate[], kind: 'source' | 'citation',
                                sourceType: string | null | undefined, text: string): string[] {
    const typed = (text ?? '').trim().toLowerCase();
    const output: string[] = [];
    for (const template of templates ?? []) {
        if (template.kind !== kind) {
            continue;
        }
        // szablon bez rodzaju pasuje do każdego źródła
        if (template.source_type && template.source_type !== (sourceType ?? null)) {
            continue;
        }
        const suggestion = applyNameTemplate(template.pattern, text);
        if (suggestion && suggestion.toLowerCase() !== typed && !output.includes(suggestion)) {
            output.push(suggestion);
        }
    }
    return output;
}

export class WebFamSourceMap {
    sources?: WebFamSource[];
    citations?: WebFamCitation[];
}

/**
 * Opis źródła w formie zdania - "nazwa, lata, autor, wydawca".
 * Wspólne dla przypisów na profilu i podpisów w galerii.
 */
export function formatSourceText(source?: WebFamSource | null): string {
    if (!source) {
        return '';
    }

    let ret = source.name ? source.name : '';
    if (source.years) {
        ret = ret + ', ' + source.years;
    }
    if (source.author) {
        ret = ret + ', ' + source.author;
    }
    if (source.publisher) {
        ret = ret + ', ' + source.publisher;
    }
    return ret;
}

/** Opis cytowania - "tytuł numer/rok, s. strony". */
export function formatCitationText(citation?: WebFamCitation | null): string {
    if (!citation) {
        return '';
    }

    // akt metrykalny bez tytułu przedstawia się rodzajem zdarzenia
    let ret = citation.title ? citation.title : citationEventTypeName(citation.type);
    if ((citation.number) && (citation.year)) {
        ret = ret + ' ' + citation.number + '/' + citation.year;
    }
    if ((citation.number) && (!citation.year)) {
        ret = ret + ' nr ' + citation.number;
    }
    if ((!citation.number) && (citation.year)) {
        ret = ret + ' ' + citation.year + ' rok';
    }
    if (citation.pages) {
        ret = ret + ', s. ' + citation.pages;
    }
    return ret;
}

export class WebFamSourceIdList {
    source!: string[];
    citation!: string[];
    constructor(source: string[], citation: string[]) {
        this.source = source;
        this.citation = citation;
    }

}
